/**
 * ★★ WO — AS TAXAS, e elas moram LONGE do painel do dia. Puro.
 *
 * ⚠⚠ POR QUE AS TAXAS NÃO ESTÃO NO PAINEL DO DIA — está medido, não é preferência.
 *
 * `docs/wo-medicao.md` §13 contou os degraus de setembro, por pessoa:
 *
 *     Jéssica: 34 leads tentados → 11 atenderam → 9 agendaram → 3 compareceram
 *     Talita : 13 leads tentados →  4 atenderam → 4 agendaram → 1 compareceu
 *
 * ★★ A taxa de comparecimento da Talita era **1 de 4 = 25%**. Um caso a mais a levaria a
 * **50%** — o dobro, sem ela fazer nada diferente. Taxa em cima de 4 eventos não mede
 * desempenho; mede sorte.
 *
 * ★ Daí as três decisões deste módulo:
 *   1. a janela é longa e CONFIGURÁVEL (`config.janelaTaxasDias`, padrão **90** por decisão
 *      do dono) — ele muda para 60 ou 30 sem código;
 *   2. degrau com denominador < 30 sai com selo **`basePequena`**;
 *   3. ★★ o funil é por **COORTE** — os MESMOS leads descendo os degraus.
 *
 * ⚠⚠ POR QUE COORTE, e isto também é medido: contado por EVENTO, o funil da Jéssica em
 * setembro **CRESCIA** — 38 tentativas → 11 atendidas → **51 agendou** → **71 compareceu**.
 * Os degraus de baixo vinham de leads que ela nunca ligou. Um painel que mostra taxa acima de
 * 100% perde a equipe no primeiro dia.
 */

import { comparecimentosNoPeriodo } from '@/leads/contagemComparecimento';
// ★ Só `creditoNegociador`. A coorte da SDR são os leads que ELA TENTOU (vêm das próprias
// interações), não os creditados a ela — num funil de produtividade a base é o trabalho
// feito, não a atribuição. `creditoSdr` chegou a ser importado aqui e saiu sem uso.
import { creditoNegociador } from '@/metas/credito';
import {
  EXPEDIENTE_PADRAO, diasEntre, expedienteDaPessoa, metaAcumulada,
} from '@/produtividade/expediente';
import { temFuncao } from '@/equipe/funcoes';
import { ETAPAS_DE_FOLLOW_UP, diaDaData, etapaDaInteracao, paraData, resultadoDaTentativa } from '@/produtividade/dia';

/** ★ Decisão do dono (2/out): a janela começa em 90 dias. */
export const JANELA_PADRAO_DIAS = 90;

/**
 * ★★ O piso de eventos para a taxa valer alguma coisa.
 *
 * ⚠ Não é número mágico: abaixo disto um caso a mais move a taxa mais que o desempenho da
 * pessoa. Com 4 eventos, 25% vira 50%. É o limiar do selo, não um corte — a taxa aparece
 * de qualquer jeito, **com o aviso ao lado**.
 */
export const PISO_BASE = 30;

export const temBasePequena = (denominador) => Number(denominador ?? 0) < PISO_BASE;

/** `{ de, ate }` da janela que termina em `ate` e cobre `dias` dias, no fuso da clínica. */
export function janelaDeDias(dias = JANELA_PADRAO_DIAS, ate = new Date()) {
  const fim = new Date(ate);
  const inicio = new Date(ate);
  inicio.setDate(inicio.getDate() - (Number(dias) || JANELA_PADRAO_DIAS));
  return { de: inicio, ate: fim };
}

/** pct sem divisão por zero: devolve null quando não há denominador. */
export const taxa = (parte, total) => (Number(total) > 0 ? (Number(parte) / Number(total)) * 100 : null);

/**
 * ★★ "Para bater a meta de X agendamentos, na sua taxa, faltam Y tentativas."
 *
 * ⚠ A conta só existe quando a taxa existe. Com taxa **zero** — a pessoa tentou e nunca
 * agendou — não há divisão possível, e inventar um número seria pior que não dar nenhum:
 * diria "faltam 0" ou estouraria. ★ Devolve `{ possivel: false, motivo }` e a tela explica.
 */
export function tentativasParaMeta({ meta = 0, agendouNaJanela = 0, leadsTentadosNaJanela = 0 }) {
  const falta = Number(meta) - Number(agendouNaJanela);
  if (!(Number(meta) > 0)) return { possivel: false, motivo: 'sem meta configurada' };
  if (falta <= 0) return { possivel: true, faltamAgendamentos: 0, faltamTentativas: 0, metaBatida: true };
  if (!(Number(leadsTentadosNaJanela) > 0)) {
    return { possivel: false, motivo: 'sem tentativas na janela para calcular a taxa', faltamAgendamentos: falta };
  }
  const t = taxa(agendouNaJanela, leadsTentadosNaJanela);
  // ★★ Taxa zero: a divisão não existe. NÃO se arredonda para 1% nem se devolve Infinity.
  if (!t || t <= 0) {
    return { possivel: false, motivo: 'nenhum agendamento na janela — sem taxa para projetar', faltamAgendamentos: falta };
  }
  return {
    possivel: true,
    metaBatida: false,
    faltamAgendamentos: falta,
    faltamTentativas: Math.ceil(falta / (t / 100)),
    taxaUsada: t,
  };
}

/**
 * As taxas acumuladas, por pessoa, por COORTE.
 *
 * @param dias          tamanho da janela (vem de `config.janelaTaxasDias`)
 * @param ate           fim da janela (padrão: agora)
 * @param interacoes    as interações da janela
 * @param leads         os leads da clínica
 * @param agendamentos  os agendamentos
 * @param equipe        `[{ id, nome, funcoes }]`
 * @param metasPorSdr   `config.metasPorSdr` — para a conta da meta
 */
export function taxasAcumuladas({
  dias = JANELA_PADRAO_DIAS,
  ate = new Date(),
  interacoes = [],
  leads = [],
  agendamentos = [],
  equipe = [],
  metasPorSdr = {},
  // ★★★ A-2c — o expediente da CLÍNICA, reserva de quem não tem horário próprio.
  expediente = EXPEDIENTE_PADRAO,
  // ★ 02/11/2026 — feriados sem meta: a meta da janela cai um dia por feriado; `[]` = a de antes
  feriados = [],
} = {}) {
  const { de, ate: fim } = janelaDeDias(dias, ate);
  /**
   * ★★★ A-2c — A META DA JANELA, pela MESMA função das ligações.
   *
   * ⚠⚠⚠ Aqui havia uma incoerência **anterior** a esta peça: ★ a meta de agendamentos era
   * **do MÊS** e a janela desta tela é de **90 dias** (`config.janelaTaxasDias`) — ★★ a conta
   * *"faltam X agendamentos"* comparava uma meta mensal com o realizado de um trimestre.
   * ⚠ Com 300/mês em produção, ela dizia "faltam 300 − (o que a pessoa agendou em 90 dias)".
   *
   * ★★★ Com a meta **por dia**, a conversão passa a ser honesta e é a mesma de todo lugar:
   * `metaAcumulada` sobre os dias da janela, com **sábado meio e domingo zero**, pelo
   * expediente **da pessoa**.
   */
  const diasDaJanela = diasEntre(de, fim);
  const docDaPessoa = new Map((equipe ?? []).map((u) => [u?.id, u]));
  const metaDaJanela = (uid) => {
    const diaria = Number(metasPorSdr?.[uid]?.agendamentosDia) || 0;
    if (diaria <= 0) return 0;
    return metaAcumulada(diaria, diasDaJanela, expedienteDaPessoa(docDaPessoa.get(uid), expediente), feriados);
  };
  const dentro = (d) => Boolean(d) && d >= de && d <= fim;
  const leadPorId = new Map((leads ?? []).map((l) => [l.id, l]));
  const idsDaEquipe = new Set((equipe ?? []).map((u) => u.id));

  // ---- coorte por pessoa: os leads que ELA tentou na janela ----
  const porPessoa = new Map();
  const garantir = (id, nome) => {
    if (!porPessoa.has(id)) {
      porPessoa.set(id, { id, nome, tentados: new Set(), atenderam: new Set(), followUpLeads: new Set(), tentativas: 0 });
    }
    return porPessoa.get(id);
  };
  for (const u of equipe ?? []) {
    if (temFuncao(u, 'sdr') || temFuncao(u, 'negociador')) garantir(u.id, u.nome ?? '');
  }

  for (const i of interacoes ?? []) {
    if (!i?.autorId || !idsDaEquipe.has(i.autorId)) continue;
    if (!dentro(paraData(i.criadoEm))) continue;
    const p = garantir(i.autorId, i.autorNome ?? '');
    if (i.tipo === 'ligacao' && i.leadId) {
      p.tentativas += 1;
      p.tentados.add(i.leadId);
      if (resultadoDaTentativa(i) === 'atendeu') p.atenderam.add(i.leadId);
    }
    const { etapa } = etapaDaInteracao(i, leadPorId.get(i.leadId));
    if (ETAPAS_DE_FOLLOW_UP.includes(etapa) && i.leadId) p.followUpLeads.add(i.leadId);
  }

  // índices de agendamento e comparecimento, para o coorte descer
  const agendouNaJanela = new Set();
  for (const a of agendamentos ?? []) {
    if (a?.leadId && dentro(paraData(a.criadoEm))) agendouNaJanela.add(a.leadId);
  }
  const compareceuNaJanela = new Set(
    comparecimentosNoPeriodo(leads, agendamentos, dentro).map(({ lead }) => lead.id),
  );

  const linhas = [...porPessoa.values()].map((p) => {
    // ★★ COORTE: cada degrau é um SUBCONJUNTO do de cima. Nunca cresce.
    const tentados = [...p.tentados];
    const atenderam = [...p.atenderam];
    const agendaram = atenderam.filter((id) => agendouNaJanela.has(id));
    const compareceram = agendaram.filter((id) => compareceuNaJanela.has(id));

    const degrau = (nome, parte, total) => ({
      nome, parte, total, taxa: taxa(parte, total), basePequena: temBasePequena(total),
    });

    // ★ A taxa do NEGOCIADOR tem outro denominador — ver o comentário abaixo.
    const fechados = (leads ?? []).filter(
      (l) => l.etapa === 'fechado' && dentro(paraData(l.dataFechamento)) && creditoNegociador(l).id === p.id,
    );
    /**
     * ★★ O DENOMINADOR DO NEGOCIADOR, e o plano §7 explica por que não é o óbvio.
     *
     * O dono pediu "fechou ÷ compareceu". ⚠ Mas a casa credita comparecimento ao **SDR
     * original** e fechamento ao **negociador** — pessoas diferentes. Medido: o JOABE fechou
     * **9** e tinha **0** comparecimentos creditados a ele. `9 ÷ 0` é indefinido.
     *
     * ★ O denominador que fecha é "comparecimentos dos leads em que ELE é o negociador" —
     * e com ele o JOABE dá **39,1%**, que é o número com sentido.
     * ⚠ Limite medido: só **32%** dos comparecimentos de setembro têm negociador definido.
     */
    const compareceramDele = [...compareceuNaJanela].filter(
      (id) => leadPorId.get(id)?.negociadorId === p.id,
    );
    const orcado = fechados.reduce((t, l) => t + (Number(l.valorOrcado) || 0), 0);
    const valor = fechados.reduce((t, l) => t + (Number(l.valorFechado) || 0), 0);

    const meta = metaDaJanela(p.id);
    return {
      id: p.id,
      nome: p.nome,
      tentativas: p.tentativas,
      funil: [
        degrau('leads tentados', tentados.length, tentados.length),
        degrau('atenderam', atenderam.length, tentados.length),
        degrau('agendaram', agendaram.length, atenderam.length),
        degrau('compareceram', compareceram.length, agendaram.length),
      ],
      pontaAPonta: { parte: compareceram.length, total: tentados.length, taxa: taxa(compareceram.length, tentados.length), basePequena: temBasePequena(tentados.length) },
      negociador: {
        fechou: fechados.length,
        compareceramDele: compareceramDele.length,
        taxaFechamento: taxa(fechados.length, compareceramDele.length),
        basePequena: temBasePequena(compareceramDele.length),
        orcado,
        valor,
        conversaoValor: taxa(valor, orcado),
        followUpLeads: p.followUpLeads.size,
      },
      metaAgendamentos: tentativasParaMeta({
        meta,
        agendouNaJanela: agendaram.length,
        leadsTentadosNaJanela: tentados.length,
      }),
    };
  });

  return { janela: { de, ate: fim, dias: Number(dias) || JANELA_PADRAO_DIAS, diaFinal: diaDaData(fim) }, porPessoa: linhas };
}
